
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import {
  Attachment, AttachmentType, BoxStatusType, Lead, LeadStatus, LeadType, ProjectsPool, Task, TaskStatus,
} from '@/types';
import { taskFatherLead } from '@/helpers';

@Component
export default class TaskMixin extends Vue {
  @Prop(Object) readonly task: Task | undefined;
  @Prop({ type: Boolean, default: false }) readonly embedded!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Il form è valido?
  isFormValid = false

  selectedStatus: 'completed' | 'error' = 'completed'

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console

    // Inizializza tutti i campi del modulo
    this.refreshTask();
  }

  // Aggiorna i campi con gli eventuali nuovi valori della Connection/Lead selezionata
  mixinRefreshTask() {
    console.log(`${this.constructor.name}: mixinRefreshTask()`); // eslint-disable-line no-console
  }
  refreshTask() { return this.mixinRefreshTask(); }

  // Il Task è valido quando sia il form che i dati extra lo sono
  get valid(): boolean {
    // Se è impossibile completare il task, la validità dei dati extra è ignorata
    return this.selectedStatus === 'error'
      ? this.isFormValid
      : this.isFormValid && this.isExtraDataValid;
  }

  // Se la validità del form cambia, la memorizza nello stato di Vuex
  @Watch('valid')
  onValidChange(val: boolean) {
    console.log(`${this.constructor.name}: onValidChange() val =`, val); // eslint-disable-line no-console

    this.$store.dispatch('updateTaskFieldsValidity', { value: val });
  }

  // I dati extra sono validi?
  get isExtraDataValid(): boolean {
    // Se non è implementato nel Componente che estende questo Mixin,
    // ritorna true.
    return true;
  }

  get selectedLead(): Lead | null {
    const childLeads: ProjectsPool = this.$store.getters.currentChildren;
    const childLeadsInfo: ProjectsPool = this.$store.getters.currentChildrenInfo;

    // Se è stato specificato un Task e questo è associato ad una Lead figlia...
    if (this.task && this.task.relationship === 'father') {
      // ... se la lead figlia non è stata ancora assegnata,
      // non ritorna nulla.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return null;

      // ... se la lead figlia è stata assegnata,
      // considera i suoi dati (se disponibili,
      // altrimenti utilizza i dati limitati di childLeadsInfo)
      return childLeads[this.task.childLeadId] ?? childLeadsInfo[this.task.childLeadId] ?? null;
    }

    // Negli altri casi ritorna la Lead selezionata memorizzata tramite Vuex
    return this.$store.getters.currentLead;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get taskVersion(): number { return this.selectedTask?.version ?? 1; }

  get taskStatus(): TaskStatus { return this.selectedTask?.status ?? null; }

  get leadType(): LeadType | null { return this.selectedLead?.type ?? this.selectedTask?.childType ?? null; }

  // Ritorma true se è in corso l'aggiornamento della Lead mostrata
  get leadUpdating(): boolean { return this.$store.getters.isProjectUpdating; }

  get leadStatus(): LeadStatus | null { return this.selectedLead?.status ?? null; }

  // Ritorna true se la selectedLead è stata ottenuta da currentChildrenInfo e non è presente in currentChildren
  get leadIsInfoOnly(): boolean {
    return (this.selectedLead && 'id' in this.selectedLead
      && this.selectedLead.id in this.$store.getters.currentChildrenInfo
      && !(this.selectedLead.id in this.$store.getters.currentChildren)) ?? false;
  }

  // Tiene sotto controllo leadUpdateing per capire quando eventualmente
  // vanno aggiornati i dati visualizzati.
  @Watch('leadUpdating')
  onLeadUpdatingChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onLeadUpdatingChange() val = ${val} oldVal = ${oldVal}`); // eslint-disable-line no-console

    // Se leadUpdating passa da true a folse,
    // la Lead ha completato l'aggiornamento e vanno aggiornati i dati visualizzati.
    if (val === false && oldVal === true) {
      // Aggiorna la Lead con i nuovi valori soltanto se è in visualizzazione readonly
      if (this.readonly) this.refreshTask();
    }
  }

  get loading(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskLoading;
  }

  get updating(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskUpdating;
  }

  get father(): Lead | null { return taskFatherLead(this.selectedTask); }

  get attachments(): Attachment[] { return this.selectedLead?.Attachments ?? []; }

  hasAttachments(fieldname: AttachmentType): boolean {
    return this.attachments.filter((a) => a.fieldname === fieldname).length > 0;
  }

  get fatherAttachments(): Attachment[] { return this.father?.Attachments ?? []; }

  fatherHasAttachments(fieldname: AttachmentType): boolean {
    return this.fatherAttachments.filter((a) => a.fieldname === fieldname).length > 0;
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  // Ridefinibile nel Componente che estende questo Mixin (come per i metodi)
  get mixinFieldsDisabled(): boolean {
    return (this.task !== null && this.task !== undefined)
      || this.selectedTask?.status !== null
      || this.readonly
      || this.loading
      || this.updating
      || this.leadIsInfoOnly;
  }
  get fieldsDisabled(): boolean {
    return this.mixinFieldsDisabled;
  }

  // Mostra le azioni nell'Alert soltanto sulle lead figlie e sui Task senza figlie
  get showAlertActions(): boolean { return this.task?.relationship !== 'father'; }

  // Mostra le azioni
  get showActions(): boolean { return !this.task && !this.readonly && !this.embedded; }

  mixinOnFieldUpdated(field: string, value: unknown = undefined) {
    if (value !== undefined) console.log(`${this.constructor.name}: mixinOnFieldUpdated(${field}, ${value})`); // eslint-disable-line no-console
    else console.log(`${this.constructor.name}: mixinOnFieldUpdated(${field})`); // eslint-disable-line no-console

    if (value !== undefined) this[field] = value;

    const update = {} as { [key: string]: unknown };

    update[field] = this[field] ?? '';

    this.$store.dispatch('updateTaskFields', update);
  }
  onFieldUpdated(field: string, value: unknown = undefined) {
    return this.mixinOnFieldUpdated(field, value);
  }

  mixinUpdateAttachmentStatus(type: AttachmentType, status: BoxStatusType) {
    console.log(`${this.constructor.name}: updateAttachmentStatus(${type}, ${status})`); // eslint-disable-line no-console

    // Se lo stato non è uno di quelli gestiti, si ferma subito
    if (type in this === false) { return; }

    (this[type] as BoxStatusType) = status;

    const update = {} as { [key: string]: unknown };

    update[type] = status;

    this.$store.dispatch('updateTaskFields', update);
  }
  updateAttachmentStatus(type: AttachmentType, status: BoxStatusType) {
    return this.mixinUpdateAttachmentStatus(type, status);
  }

  mixinOnSwitchChange(name: string) {
    console.log(`${this.constructor.name}: onSwitchChange(${name})`); // eslint-disable-line no-console

    // Se lo switch non esiste si ferma subito
    if (name in this === false) { return; }

    // Il valore del modello dello switch è già stato aggiornato.
    // Proseguo aggiornando eventuali valori collegati e preparo l'aggiornamento tramite API.

    const update = {} as { [key: string]: unknown };

    update[name] = this[name];

    // Modifico eventuali campi collegati
    switch (name) {
      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }
  onSwitchChange(name: string) {
    return this.mixinOnSwitchChange(name);
  }

  getForm(): (Vue & { validate: () => boolean; reset: () => boolean }) | undefined {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    this.$emit('reset');
  }
}
